import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { GraduationCap, Award, HeartPulse, Sparkles } from "lucide-react";

const HIGHLIGHTS = [
  {
    icon: GraduationCap,
    title: "Clinical Nutrition Specialist",
    desc: "M.Sc. Clinical Nutrition & Dietetics with advanced training in therapeutic diets.",
  },
  {
    icon: Award,
    title: "8+ Years of Practice",
    desc: "1,200+ transformations across PCOS, thyroid, diabetes and sustainable fat loss.",
  },
  {
    icon: HeartPulse,
    title: "Client-First Philosophy",
    desc: "No fads. No shaming. Just evidence-led plans designed around real Indian lives.",
  },
  {
    icon: Sparkles,
    title: "Whole-Person Wellness",
    desc: "Nutrition, mindset, sleep and movement woven together — the way change actually sticks.",
  },
];

export default function Founder() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);
  const captionY = useTransform(scrollYProgress, [0, 1], ["10%", "-10%"]);

  return (
    <section
      ref={ref}
      id="founder"
      data-testid="founder-section"
      className="relative section-pad"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-14 items-center">
        {/* Portrait — editorial spotlight treatment */}
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9, ease: [0.2, 0.8, 0.2, 1] }}
          className="lg:col-span-5 order-2 lg:order-1 relative"
        >
          <div
            className="relative aspect-[4/5] rounded-[28px] overflow-hidden founder-clip gradient-border"
            data-testid="founder-portrait"
          >
            {/* Actual photo — untouched, object-cover, top-anchored so face stays framed */}
            <motion.img
              src="/uploads/pooja_final.jpeg?v=2"
              alt="Dt. Pooja A H — Founder & Head Dietician, DietDotIn"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ y: imgY, objectPosition: "50% 20%" }}
              loading="lazy"
            />

            {/* Editorial spotlight + film grain */}
            <div className="founder-spot" />
            <div className="founder-grain" />

            {/* Edge vignette — blends studio white into brand dark without altering photo */}
            <div className="pointer-events-none absolute inset-0 shadow-[inset_0_0_120px_60px_rgba(20,3,38,0.65)]" />
            <div className="pointer-events-none absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-[#140326]/70 via-[#140326]/20 to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-1/3 bg-gradient-to-l from-[#140326]/70 via-[#140326]/20 to-transparent" />
            <div className="pointer-events-none absolute inset-x-0 top-0 h-1/3 bg-gradient-to-b from-[#140326]/80 via-[#140326]/25 to-transparent" />

            {/* Bottom fade so caption sits cleanly */}
            <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-[#140326] via-[#140326]/70 to-transparent pointer-events-none" />

            {/* Subtle brand tint wash */}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-[#7A2BFF]/10 via-transparent to-[#27C6FF]/8 mix-blend-color" />

            {/* Vertical rule + serial */}
            <div className="absolute top-6 right-6 flex flex-col items-end gap-2 text-[10px] tracking-[0.35em] uppercase text-white/70 font-heading">
              <span>Dt · Pooja · A · H</span>
              <span className="block w-px h-14 bg-gradient-to-b from-white/70 via-white/20 to-transparent" />
            </div>

            {/* Caption — pinned inside frame */}
            <div className="absolute bottom-5 left-5 right-5">
              <div className="glass-strong rounded-2xl p-4 flex items-center justify-between gap-4">
                <div>
                  <div className="text-white font-display font-bold text-lg leading-none">
                    Dt. Pooja A H
                  </div>
                  <div className="text-[#CFAEFF] text-xs mt-1.5 tracking-wide">
                    Founder &middot; Head Dietician
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[#27C6FF] font-heading text-[10px] uppercase tracking-[0.28em]">
                    Since
                  </div>
                  <div className="text-white font-display font-bold text-lg leading-none mt-1">
                    2017
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Ambient glow */}
          <div className="absolute -z-10 inset-0 blur-3xl opacity-70 pointer-events-none">
            <div className="absolute top-10 -left-10 w-40 h-40 rounded-full bg-[#7A2BFF]/40" />
            <div className="absolute bottom-10 -right-10 w-40 h-40 rounded-full bg-[#27C6FF]/40" />
          </div>
        </motion.div>

        {/* Copy column */}
        <motion.div
          style={{ y: captionY }}
          className="lg:col-span-7 order-1 lg:order-2"
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7 }}
            className="flex items-center gap-4 mb-5"
          >
            <span className="text-[#27C6FF] uppercase tracking-[0.3em] text-xs font-heading">
              Meet the Head Dietician
            </span>
            <span className="hidden sm:block h-px w-16 bg-gradient-to-r from-[#27C6FF] to-transparent" />
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, delay: 0.05 }}
            className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05]"
          >
            Helping you create
            <br />
            <span className="gradient-text">sustainable transformation.</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="mt-6 text-[#D8CCFF] text-base md:text-lg leading-relaxed font-body max-w-2xl"
          >
            A passionate clinical nutritionist who believes lasting
            transformation is built on{" "}
            <strong className="text-white">
              willpower, discipline and consistency
            </strong>{" "}
            — supported by the right plan, the right coaching, and sustainable
            habits. Dt. Pooja goes beyond calorie counting, blending
            nutrient-dense nutrition, mindful eating and lifestyle coaching to
            help clients heal, transform and reconnect with their healthiest
            selves — through evidence-based guidance and compassionate support.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, delay: 0.22 }}
            className="mt-5 text-[#CFAEFF] italic text-base md:text-lg font-body max-w-2xl border-l-2 border-[#27C6FF]/60 pl-5"
          >
            &ldquo;True transformation doesn&rsquo;t come from perfect diets and
            workouts — it comes from the willpower and discipline to stay
            consistent long after motivation fades.&rdquo;
          </motion.p>

          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            {HIGHLIGHTS.map((h, i) => {
              const Icon = h.icon;
              return (
                <motion.div
                  key={h.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.55, delay: 0.05 * i }}
                  whileHover={{ y: -4 }}
                  className="glass rounded-2xl p-5 flex items-start gap-4 hover:neon-violet transition-shadow duration-500"
                  data-testid={`founder-highlight-${h.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#7A2BFF] to-[#27C6FF] flex items-center justify-center flex-shrink-0">
                    <Icon size={20} className="text-white" />
                  </div>
                  <div>
                    <div className="font-heading font-semibold text-white text-sm md:text-base">
                      {h.title}
                    </div>
                    <div className="text-[#CFAEFF] text-xs md:text-sm mt-1">
                      {h.desc}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <a
            href="#booking"
            data-testid="founder-cta-book"
            className="mt-10 inline-flex btn-primary px-7 py-3.5 rounded-full font-heading font-semibold"
          >
            Book a consultation
          </a>
        </motion.div>
      </div>
    </section>
  );
}
